import React, { useState } from 'react';

// Define types for our component
interface JsonToRssProps { }

// RSS feed types
interface RssChannelInfo {
    title: string;
    link: string;
    description: string;
    language: string;
    copyright?: string;
    pubDate?: string;
    generator?: string;
    managingEditor?: string;
    webMaster?: string;
    category?: string;
    ttl?: number;
}

interface RssItem {
    title: string;
    link: string;
    description: string;
    author?: string;
    categories?: string[];
    pubDate?: string;
    guid?: string;
    enclosure?: {
        url: string;
        length: number;
        type: string;
    };
}

interface RssFeed {
    channelInfo: RssChannelInfo;
    items: RssItem[];
}

// Sample data for demonstration
const sampleData: RssFeed = {
    channelInfo: {
        title: "My Tech Blog",
        link: "https://mytechblog.example.com",
        description: "Latest tech news and tutorials",
        language: "en-us",
        copyright: "Copyright 2025 My Tech Blog",
        pubDate: new Date().toUTCString(),
        generator: "Custom RSS Generator",
        ttl: 60
    },
    items: [
        {
            title: "Getting Started with TypeScript",
            link: "https://mytechblog.example.com/typescript-intro",
            description: "Learn the basics of TypeScript and how to set up your first project.",
            author: "jane.doe@example.com (Jane Doe)",
            pubDate: new Date().toUTCString(),
            guid: "https://mytechblog.example.com/typescript-intro",
            categories: ["TypeScript", "Programming", "Web Development"]
        },
        {
            title: "Advanced React Patterns",
            link: "https://mytechblog.example.com/advanced-react",
            description: "Dive deep into advanced React patterns including compound components and render props.",
            author: "john.smith@example.com (John Smith)",
            pubDate: new Date(Date.now() - 86400000).toUTCString(), // Yesterday
            guid: "https://mytechblog.example.com/advanced-react",
            categories: ["React", "JavaScript", "Web Development"]
        },
        {
            title: "Setting Up a CI/CD Pipeline",
            link: "https://mytechblog.example.com/cicd-pipeline",
            description: "Learn how to set up a continuous integration and deployment pipeline for your projects.",
            author: "mike.jones@example.com (Mike Jones)",
            pubDate: new Date(Date.now() - 172800000).toUTCString(), // 2 days ago
            guid: "https://mytechblog.example.com/cicd-pipeline",
            categories: ["DevOps", "CI/CD", "Automation"]
        }
    ]
};

const JsonToRss: React.FC<JsonToRssProps> = () => {
    // State for the JSON input
    const [jsonInput, setJsonInput] = useState<string>('');
    // State for the RSS output
    const [rssOutput, setRssOutput] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for the file name
    const [fileName, setFileName] = useState<string>('feed');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing advanced options
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isRssGenerated, setIsRssGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);
    // State for RSS preview
    const [showPreview, setShowPreview] = useState<boolean>(true);
    // State for RSS version
    const [rssVersion, setRssVersion] = useState<string>("2.0");
    // State for pretty print XML
    const [prettyPrint, setPrettyPrint] = useState<boolean>(true);

    // Handle JSON input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setJsonInput(e.target.value);
        setError('');
        setIsRssGenerated(false);
    };

    // Handle file name change
    const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(e.target.value);
    };

    // Use example data
    const useExampleData = () => {
        setJsonInput(JSON.stringify(sampleData, null, 2));
        setError('');
        setIsRssGenerated(false);
        setRssOutput('');
    };

    // Beautify/Format JSON
    const beautifyJson = () => {
        try {
            if (!jsonInput.trim()) {
                setError('Please enter JSON data to format');
                return;
            }

            const parsedJson = JSON.parse(jsonInput);
            const beautified = JSON.stringify(parsedJson, null, 2);
            setJsonInput(beautified);
            setError('');
        } catch (e) {
            setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    // Validate JSON
    const validateJson = (jsonString: string): boolean => {
        try {
            JSON.parse(jsonString);
            return true;
        } catch (e) {
            return false;
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setJsonInput('');
        setRssOutput('');
        setError('');
        setFileName('feed');
        setRssVersion("2.0");
        setPrettyPrint(true);
        setIsRssGenerated(false);
    };

    // Escape special XML characters
    const escapeXml = (unsafe: string): string => {
        return unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    };

    // Generate RSS XML from JSON data
    const generateRssXml = (feed: RssFeed): string => {
        let xml = `<?xml version="1.0" encoding="UTF-8" ?>\n`;
        xml += `<rss version="${rssVersion}">\n`;
        xml += `  <channel>\n`;

        // Add channel information
        const channelInfo = feed.channelInfo;
        xml += `    <title>${escapeXml(channelInfo.title)}</title>\n`;
        xml += `    <link>${escapeXml(channelInfo.link)}</link>\n`;
        xml += `    <description>${escapeXml(channelInfo.description)}</description>\n`;
        xml += `    <language>${escapeXml(channelInfo.language)}</language>\n`;

        if (channelInfo.copyright) {
            xml += `    <copyright>${escapeXml(channelInfo.copyright)}</copyright>\n`;
        }

        if (channelInfo.pubDate) {
            xml += `    <pubDate>${escapeXml(channelInfo.pubDate)}</pubDate>\n`;
        }

        if (channelInfo.generator) {
            xml += `    <generator>${escapeXml(channelInfo.generator)}</generator>\n`;
        }

        if (channelInfo.managingEditor) {
            xml += `    <managingEditor>${escapeXml(channelInfo.managingEditor)}</managingEditor>\n`;
        }

        if (channelInfo.webMaster) {
            xml += `    <webMaster>${escapeXml(channelInfo.webMaster)}</webMaster>\n`;
        }

        if (channelInfo.category) {
            xml += `    <category>${escapeXml(channelInfo.category)}</category>\n`;
        }

        if (channelInfo.ttl) {
            xml += `    <ttl>${channelInfo.ttl}</ttl>\n`;
        }

        // Add items
        feed.items.forEach(item => {
            xml += `    <item>\n`;
            xml += `      <title>${escapeXml(item.title)}</title>\n`;
            xml += `      <link>${escapeXml(item.link)}</link>\n`;
            xml += `      <description>${escapeXml(item.description)}</description>\n`;

            if (item.author) {
                xml += `      <author>${escapeXml(item.author)}</author>\n`;
            }

            if (item.categories && item.categories.length > 0) {
                item.categories.forEach(category => {
                    xml += `      <category>${escapeXml(category)}</category>\n`;
                });
            }

            if (item.pubDate) {
                xml += `      <pubDate>${escapeXml(item.pubDate)}</pubDate>\n`;
            }

            if (item.guid) {
                xml += `      <guid isPermaLink="true">${escapeXml(item.guid)}</guid>\n`;
            }

            if (item.enclosure) {
                xml += `      <enclosure url="${escapeXml(item.enclosure.url)}" length="${item.enclosure.length}" type="${escapeXml(item.enclosure.type)}" />\n`;
            }

            xml += `    </item>\n`;
        });

        xml += `  </channel>\n`;
        xml += `</rss>`;

        return xml;
    };

    // Convert JSON to minified XML (no pretty print)
    const generateMinifiedRssXml = (feed: RssFeed): string => {
        let xml = `<?xml version="1.0" encoding="UTF-8" ?>`;
        xml += `<rss version="${rssVersion}">`;
        xml += `<channel>`;

        // Add channel information
        const channelInfo = feed.channelInfo;
        xml += `<title>${escapeXml(channelInfo.title)}</title>`;
        xml += `<link>${escapeXml(channelInfo.link)}</link>`;
        xml += `<description>${escapeXml(channelInfo.description)}</description>`;
        xml += `<language>${escapeXml(channelInfo.language)}</language>`;

        if (channelInfo.copyright) {
            xml += `<copyright>${escapeXml(channelInfo.copyright)}</copyright>`;
        }

        if (channelInfo.pubDate) {
            xml += `<pubDate>${escapeXml(channelInfo.pubDate)}</pubDate>`;
        }

        if (channelInfo.generator) {
            xml += `<generator>${escapeXml(channelInfo.generator)}</generator>`;
        }

        if (channelInfo.managingEditor) {
            xml += `<managingEditor>${escapeXml(channelInfo.managingEditor)}</managingEditor>`;
        }

        if (channelInfo.webMaster) {
            xml += `<webMaster>${escapeXml(channelInfo.webMaster)}</webMaster>`;
        }

        if (channelInfo.category) {
            xml += `<category>${escapeXml(channelInfo.category)}</category>`;
        }

        if (channelInfo.ttl) {
            xml += `<ttl>${channelInfo.ttl}</ttl>`;
        }

        // Add items
        feed.items.forEach(item => {
            xml += `<item>`;
            xml += `<title>${escapeXml(item.title)}</title>`;
            xml += `<link>${escapeXml(item.link)}</link>`;
            xml += `<description>${escapeXml(item.description)}</description>`;

            if (item.author) {
                xml += `<author>${escapeXml(item.author)}</author>`;
            }

            if (item.categories && item.categories.length > 0) {
                item.categories.forEach(category => {
                    xml += `<category>${escapeXml(category)}</category>`;
                });
            }

            if (item.pubDate) {
                xml += `<pubDate>${escapeXml(item.pubDate)}</pubDate>`;
            }

            if (item.guid) {
                xml += `<guid isPermaLink="true">${escapeXml(item.guid)}</guid>`;
            }

            if (item.enclosure) {
                xml += `<enclosure url="${escapeXml(item.enclosure.url)}" length="${item.enclosure.length}" type="${escapeXml(item.enclosure.type)}" />`;
            }

            xml += `</item>`;
        });

        xml += `</channel>`;
        xml += `</rss>`;

        return xml;
    };

    // Validate RSS feed structure
    const validateRssFeedStructure = (data: any): { valid: boolean; message?: string } => {
        // Check if channelInfo exists and has required fields
        if (!data.channelInfo) {
            return { valid: false, message: 'Missing channelInfo object' };
        }

        const requiredChannelFields = ['title', 'link', 'description', 'language'];
        for (const field of requiredChannelFields) {
            if (!data.channelInfo[field]) {
                return { valid: false, message: `Missing required channel field: ${field}` };
            }
        }

        // Check if items array exists
        if (!data.items || !Array.isArray(data.items)) {
            return { valid: false, message: 'Missing items array or items is not an array' };
        }

        // Check each item for required fields
        const requiredItemFields = ['title', 'link', 'description'];
        for (let i = 0; i < data.items.length; i++) {
            const item = data.items[i];
            for (const field of requiredItemFields) {
                if (!item[field]) {
                    return { valid: false, message: `Item at index ${i} is missing required field: ${field}` };
                }
            }
        }

        return { valid: true };
    };

    // Convert JSON to RSS
    const convertJsonToRss = () => {
        setError('');
        setIsLoading(true);
        setIsRssGenerated(false);
        setRssOutput('');

        if (!jsonInput.trim()) {
            setError('Please enter JSON data');
            setIsLoading(false);
            return;
        }

        if (!validateJson(jsonInput)) {
            setError('Invalid JSON format. Please check your input.');
            setIsLoading(false);
            return;
        }

        try {
            const jsonData = JSON.parse(jsonInput);

            // Validate the structure of the RSS feed
            const validation = validateRssFeedStructure(jsonData);
            if (!validation.valid) {
                setError(`Invalid RSS feed structure: ${validation.message}`);
                setIsLoading(false);
                return;
            }

            // Generate RSS XML
            const rssXml = prettyPrint ?
                generateRssXml(jsonData as RssFeed) :
                generateMinifiedRssXml(jsonData as RssFeed);

            setRssOutput(rssXml);
            setIsRssGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error generating RSS: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Copy RSS to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(rssOutput)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Download RSS file
    const downloadRss = () => {
        const blob = new Blob([rssOutput], { type: 'application/rss+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${fileName || 'feed'}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Validate RSS feed against RSS specifications
    const validateRss = () => {
        // This is a simplified validation
        if (!rssOutput) {
            setError('No RSS feed to validate');
            return;
        }

        try {
            // Check for basic XML parsing errors
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(rssOutput, "text/xml");

            // Check if parsing resulted in errors
            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                throw new Error('XML parsing error: ' + parserError.textContent);
            }

            // Check for required RSS elements
            const rssElement = xmlDoc.querySelector('rss');
            if (!rssElement) {
                throw new Error('Missing root RSS element');
            }

            const channelElement = xmlDoc.querySelector('channel');
            if (!channelElement) {
                throw new Error('Missing channel element');
            }

            const requiredElements = ['title', 'link', 'description'];
            for (const element of requiredElements) {
                if (!channelElement.querySelector(element)) {
                    throw new Error(`Missing required channel element: ${element}`);
                }
            }

            // If we get here, validation passed
            alert('RSS validation successful! The feed appears to be valid.');
        } catch (e) {
            setError(`RSS validation failed: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">JSON to RSS Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert JSON data to standard RSS feeds. Paste your JSON data below to generate a valid RSS XML feed compatible with feed readers.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="jsonInput" className="font-medium text-gray-700">
                                JSON Input
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleData}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={beautifyJson}
                                >
                                    Format JSON
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setJsonInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <textarea
                            id="jsonInput"
                            className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={jsonInput}
                            onChange={handleInputChange}
                            placeholder={`Paste your JSON data here, e.g.,\n{\n  "channelInfo": {\n    "title": "My Blog",\n    "link": "https://myblog.com",\n    "description": "Blog about tech",\n    "language": "en-us"\n  },\n  "items": [\n    {\n      "title": "First Post",\n      "link": "https://myblog.com/first",\n      "description": "This is my first post"\n    }\n  ]\n}`}
                        />
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="mb-4">
                            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                                File Name
                            </label>
                            <input
                                type="text"
                                id="fileName"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={fileName}
                                onChange={handleFileNameChange}
                                placeholder="Enter file name (without extension)"
                            />
                        </div>

                        <div className="mb-4">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                            >
                                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showAdvanced && (
                            <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                                <div>
                                    <label htmlFor="rssVersion" className="block text-sm font-medium text-gray-700 mb-1">
                                        RSS Version
                                    </label>
                                    <select
                                        id="rssVersion"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        value={rssVersion}
                                        onChange={(e) => setRssVersion(e.target.value)}
                                    >
                                        <option value="2.0">RSS 2.0 (Recommended)</option>
                                        <option value="1.0">RSS 1.0 (RDF)</option>
                                        <option value="0.92">RSS 0.92 (Legacy)</option>
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        RSS 2.0 is the most widely supported format
                                    </p>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="prettyPrint"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={prettyPrint}
                                        onChange={(e) => setPrettyPrint(e.target.checked)}
                                    />
                                    <label htmlFor="prettyPrint" className="ml-2 block text-sm text-gray-700">
                                        Pretty Print XML (formatted with line breaks and indentation)
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="showPreview"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={showPreview}
                                        onChange={(e) => setShowPreview(e.target.checked)}
                                    />
                                    <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                                        Show Preview
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertJsonToRss}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Convert to RSS'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            RSS Output
                        </label>

                        {isRssGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={copyToClipboard}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied ? 'Copied!' : 'Copy RSS'}
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={downloadRss}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={validateRss}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Validate
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isRssGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 11-2 0 1 1 0 012 0z" />
                                    </svg>
                                    <p>
                                        RSS feed will appear here after conversion.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isRssGenerated && (
                            <div className="flex-grow flex flex-col">
                                {showPreview ? (
                                    <div className="mb-3">
                                        <div className="mb-2 flex justify-between items-center">
                                            <h3 className="font-medium text-gray-800">Preview:</h3>
                                            <button
                                                className="text-xs text-gray-600 hover:text-gray-800"
                                                onClick={() => setShowPreview(false)}
                                            >
                                                Hide Preview
                                            </button>
                                        </div>
                                        <div className="border border-gray-200 rounded-md h-48 overflow-auto p-3 mb-3">
                                            <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                                                {rssOutput}
                                            </pre>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-3">
                                        <button
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                            onClick={() => setShowPreview(true)}
                                        >
                                            Show Preview
                                        </button>
                                    </div>
                                )}

                                <div className="flex-grow flex flex-col">
                                    <label htmlFor="rssOutput" className="text-sm font-medium text-gray-700 mb-1">
                                        RSS Code:
                                    </label>
                                    <textarea
                                        id="rssOutput"
                                        className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                                        value={rssOutput}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {isRssGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">RSS Feed Successfully Generated!</p>
                            </div>
                            <p className="text-sm">
                                Your RSS feed is ready to use. You can now download the XML file or copy the code to your clipboard.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About JSON to RSS Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
                        <p className="text-gray-600">
                            This tool transforms your structured JSON data into standard RSS feed format. The converter validates your JSON structure,
                            generates the appropriate XML tags, and ensures compatibility with feed readers and aggregators.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Required JSON Structure</h3>
                        <p className="text-gray-600 mb-2">
                            Your JSON input must follow this structure:
                        </p>
                        <pre className="bg-gray-50 p-3 rounded-md text-xs font-mono overflow-auto text-gray-700">
                            {`{
  "channelInfo": {
    "title": "Feed Title",          // Required
    "link": "https://example.com",  // Required
    "description": "Description",   // Required
    "language": "en-us",            // Required
    "copyright": "Optional",        // Optional
    "pubDate": "Tue, 20 May 2025",  // Optional
    "generator": "Optional",        // Optional
    // Other optional channel elements
  },
  "items": [
    {
      "title": "Item Title",        // Required
      "link": "https://example.com/item", // Required
      "description": "Content",     // Required
      "author": "Optional",         // Optional
      "pubDate": "Tue, 20 May 2025", // Optional
      "guid": "https://example.com/item", // Optional
      "categories": ["cat1", "cat2"] // Optional
      // Other optional item elements
    },
    // More items...
  ]
}`}
                        </pre>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Standards Compliant</strong> - Generates valid RSS according to the chosen specification</li>
                            <li><strong>Special Character Handling</strong> - Automatically escapes XML special characters</li>
                            <li><strong>Multiple RSS Versions</strong> - Support for RSS 2.0, 1.0, and 0.92</li>
                            <li><strong>Pretty Printing</strong> - Option to generate formatted XML with proper indentation</li>
                            <li><strong>Validation</strong> - Checks that the generated RSS feed conforms to standards</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Always include the required fields in both channelInfo and items</li>
                            <li>Use ISO-8601 dates or RFC-822 dates for best compatibility</li>
                            <li>Keep item descriptions concise for better reader compatibility</li>
                            <li>For podcasts, use the enclosure tag to include audio files</li>
                            <li>Add categories to help users find your content more easily</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JsonToRss;